import fetch from './fetch'
import config from 'shared/config'
import { type ErrorCodeType } from 'shared/configStatuses'

import { type OptimisationStrategy } from 'src/budgetOptimiserV2/api/transformations/transform'
import { type RecommendationStage } from 'shared/assetsBucket/recommendations'
import { type Platforms } from './accounts'

const analyticsRequestBaseUrl = config('ANALYTICS_REQUEST_BASE_URL') as string
const client =
  analyticsRequestBaseUrl === config('NEXT_PUBLIC_API_URL')
    ? fetch()
    : fetch(undefined, { baseUrl: analyticsRequestBaseUrl })

export const paths = {
  analytics: '/analytics',
  dimensionValues: '/analytics/discovery/dimension-values'
}

type DateField = 'date' | 'target_date' | 'timestamp' | 'publish_time'
export type Direction = 'DESC' | 'ASC'

export interface Filter {
  field: string
  operator: string
  logicalOperator?: string
  value?: string[] | string | number | boolean
}

interface AttributionModelPerformanceSummary {
  adSpend: number
  conversions: number
  revenue: number
  roas?: number
  costPerAction?: number
}

interface AttributionModelsMetricsResp extends Array<AttributionModelPerformanceSummary> {}
export type ReportType =
  | 'channel_grouping_performance_report_v2'
  | 'performance_summary_ia'
  | 'detailed_report_ia'
  | 'detailed_report_dda'
  | 'detailed_report_lnda'
  | 'detailed_report_ga4'
  | 'event_feed'
  | 'attribution'
  | 'attribution_v2'
  | 'connectors_client_configs_status_ui'
  | 'transform_client_configs_status_ui'
  | 'connectors_config_status_ui'
  | 'transform_config_status_ui'
  | 'attribution_dag'
  | 'attribution_client_configs_status_ui'
  | 'attribution_model_config_status_ui'
  | 'transform_dag'
  | 'budget_optimiser_non_native_dag'
  | 'budget_optimiser_native_dag'
  | 'budget_optimiser_client_configs_status_ui'
  | 'budget_optimiser_config_status_ui'
  | 'transformed_events'
  | 'transformed_events_timestamp'
  | 'events_statuses'
  | 'events_statuses_time_series'
  | 'config_errors'
  | 'profit'
  | 'connectors_dag'
  | ChannelGroupingReportType
  | 'reporting_base'
  | 'reporting_base_v2'
  | 'all_marketing_data'
  | 'all_analytics_data'
  | 'arima_plus_output'
  | 'creative_insights_report'
  | 'creative_insights_conversions_report'
  | 'creative_insights_distinct_values_report'
  | 'search_synergies_search_term_opportunities'
  | 'search_synergies_paid_organic_search_term_performance_gads'
  | 'search_synergies_cost'
  | 'search_synergies_landing_page_experience'
  | 'pmm_product_base_metrics'
  | 'pmm_item_brand_level_report'
  | 'pmm_brand_market_level_report'
  | 'pmm_item_category_level_report'
  | 'pmm_category_market_level_report'
  | 'pmm_item_sku_level_report'
  | 'pmm_sku_market_level_report'
  | 'black_friday_report'
  | 'gads_keywords_quality_score'
  | 'bo_scenario'

export type ChannelGroupingReportType =
  | 'analytics'
  | 'ga4'
  | 'ga4_sessions'
  | 'facebook'
  | 'google'
  | 'gads'
  | 'dv360'
  | 'linkedin'
  | 'linkedin_ads'
  | 'tiktok'
  | 'pinterest'
  | 'snapchat'

export interface AnalyticsReqField {
  name: string
  aggregation?: string
}

export interface AnalyticsReqOrderBy {
  fields: string[]
  direction: Direction
}
export interface AnalyticsReq {
  id?: string
  collection:
    | 'attribution'
    | 'standard_reports'
    | 'event_feed'
    | 'status'
    | 'enhanced_signals'
    | 'channel_grouping'
    | 'creative_report'
  report: ReportType
  singleRow?: boolean
  clientId: number
  cursor?: number
  limit?: number
  fields?: AnalyticsReqField[]
  filters?: Filter[]
  orderBy?: AnalyticsReqOrderBy
  groupBy?: string[]
  targetCurrencyCode?: string
  useCache?: boolean
}

export type AnalyticsRespRecord = Record<string, string | number>

export interface AnalyticsMetadataField {
  field: string
  name: string
  type: string
}

export interface AnalyticsMetadata {
  fields: AnalyticsMetadataField[]
}

export interface AnalyticsResp {
  data: AnalyticsRespRecord[]
  metaData?: AnalyticsMetadata
  nextCursor: number | string
  previousCursor: number | string
  statusCode: number
  totalRows: number
}

export interface DimensionValuesReq {
  clientId: number
  collection: string
  report: string
  dimension: string
  configId?: string
  limit?: number
  includeNulls?: boolean
}

/** A discovered dimension value. Numeric dimensions such as hour arrive as JSON numbers. */
export type DimensionPicklistValue = string | number

export interface DimensionValuesResp {
  dimension: string
  values: DimensionPicklistValue[]
  totalValues: number
  truncated: boolean
  totalRows: number
}

export interface DimensionValuesWithNullsResp extends Omit<DimensionValuesResp, 'values'> {
  values: Array<string | null>
}

export interface DatesInAttributionReportResp {
  distinctDate: string
}

export interface DatesInBudgetOptimiserOptimalsResp {
  distinctTargetDate: string
}

export interface AttributionResponse {
  channel: string
  date: string | null // this is optional for channel level reporting
  ddaV2Conversions: number | null
  ddaV2Revenue: number | null
  iaUserBasedReallocationConversions: number | null
  iaUserBasedReallocationRevenue: number | null
  regressionBasedAttributionConversions: number | null
  regressionBasedAttributionRevenue: number | null
  impressions: number | null
  lndaConversions: number | null
  lndaRevenue: number | null
  ga4DefaultAttributionConversions: number | null
  ga4DefaultAttributionRevenue: number | null
  ga4LndaAttributionConversions: number | null
  ga4LndaAttributionRevenue: number | null
  spend: number | null
}

export interface AttributionChannelAggregatedResponse {
  channel: string
  channelId: string
  sumSpend: number | null
  sumImpressions: number | null
  sumDdaV2Conversions: number | null
  sumDdaV2Revenue: number | null
  sumIaUserBasedReallocationConversions: number | null
  sumIaUserBasedReallocationRevenue: number | null
  sumRegressionBasedAttributionConversions: number | null
  sumRegressionBasedAttributionRevenue: number | null
  sumLndaConversions: number | null
  sumLndaRevenue: number | null
  sumGa4DefaultAttributionConversions: number | null
  sumGa4DefaultAttributionRevenue: number | null
  sumGa4LndaAttributionConversions: number | null
  sumGa4LndaAttributionRevenue: number | null
  avgQualityOfFitRevenue?: number | null
  avgQualityOfFitConversions?: number | null
  avgDataSufficiency?: number | null
  avgVariationStrength?: number | null
  avgCompositeConfidenceRevenue?: number | null
  avgCompositeConfidenceConversions?: number | null
  avgStabilityScoreRevenue?: number | null
  avgStabilityScoreConversions?: number | null
  targetCurrencyCode?: string
}

export interface AttributionDateChannelAggregatedResponse {
  channel: string
  date: string | null
  sumSpend: number | null
  sumImpressions: number | null
  sumDdaV2Conversions: number | null
  sumDdaV2Revenue: number | null
  sumIaUserBasedReallocationConversions: number | null
  sumIaUserBasedReallocationRevenue: number | null
  sumRegressionBasedAttributionConversions: number | null
  sumRegressionBasedAttributionRevenue: number | null
  sumLndaConversions: number | null
  sumLndaRevenue: number | null
  sumGa4DefaultAttributionConversions: number | null
  sumGa4DefaultAttributionRevenue: number | null
  sumGa4LndaAttributionConversions: number | null
  sumGa4LndaAttributionRevenue: number | null
}

export interface AttributionChannelAggregatedResponseV2 {
  channelId: string
  channel: string
  sumSpend: number | null
  sumImpressions: number | null
  sumGa4Sessions: number | null
  sumGa4DefaultAttributionConversions: number | null
  sumGa4DefaultAttributionRevenue: number | null
  sumGa4LndaAttributionConversions: number | null
  sumGa4LndaAttributionRevenue: number | null
  sumRegressionBasedAttributionConversions: number | null
  sumRegressionBasedAttributionRevenue: number | null
  avgDataSufficiency: number | null
  avgVariationStrength: number | null
  avgQualityOfFitRevenue: number | null
  avgQualityOfFitConversions: number | null
  avgStabilityScoreRevenue: number | null
  avgStabilityScoreConversions: number | null
  avgCompositeConfidenceRevenue: number | null
  avgCompositeConfidenceConversions: number | null
}

export interface AttributionDateChannelAggregatedResponseV2 extends AttributionChannelAggregatedResponseV2 {
  date: string
}

export const BudgetOptimiserOptimalsPlatformsEnum = {
  SNAPCHAT: 'snapchat',
  LINKEDIN: 'linkedin',
  TIKTOK: 'tiktok',
  FACEBOOK: 'facebook',
  GOOGLE_ADS: 'googleads',
  BING: 'bing',
  DV360: 'dv360',
  CRITEO: 'criteo',
  AMAZON: 'amazon',
  PINTEREST: 'pinterest',
  CHATGPT_ADS: 'chatgpt_ads',
  CUSTOM: 'custom'
} as const

export type BudgetOptimiserOptimalsPlatforms =
  (typeof BudgetOptimiserOptimalsPlatformsEnum)[keyof typeof BudgetOptimiserOptimalsPlatformsEnum]

export interface FeedRespJsonPayloadData extends Record<string, string | number> {}

export interface FeedRespJsonPayload {
  ruleId: string
  data: FeedRespJsonPayloadData[]
  stage?: RecommendationStage
  totalNumEntities: number
  totalNumEntitiesWithIssues: number
}

export interface FeedResp {
  clientId: number
  message: string
  messageId: string
  platform: Platforms
  publishTime: string
  sourceService: string
  subscriptionName: string
  tags: string[]
  timestamp: string
  title: string
  jsonPayload: FeedRespJsonPayload
}

export interface FeedDistinctPlatforminResp {
  distinctPlatform: Platforms
}

export interface ChannelGroupingPerformanceStandardReportResp {
  customChannelGrouping: string
  sumTransactions: number | null
  sumAdCost: number | null
  date: string
  marketChannelGrouping: string
  sumTransactionRevenue: number | null
}

export type ChannelGroupingPerformanceTotalsResp = Pick<
  ChannelGroupingPerformanceStandardReportResp,
  'sumTransactions' | 'sumAdCost' | 'sumTransactionRevenue'
>

export interface ChannelGroupingPerformanceTotalsGroupedByDateResp extends ChannelGroupingPerformanceTotalsResp {
  date: string
}

export interface ReportingBaseReportResp {
  channelGrouping: string
  sumConversionValue: number | null
  sumConversions: number | null
  sumCost: number | null
  marketName: string
  date: string
  sumClicks: number | null
  sumImpressions: number | null
  sumVideoViews: number | null
  sumViewableImpressions: number | null
  sumSessions: number | null
  sumDailyTarget: number | null
  sumDailyBudget: number | null
}

export interface ReportingBaseConversionEventResp {
  conversionSource: string
  conversionName: string
  sumConversions: number | null
}

export type ReportingBaseFilterParametersResp = Pick<ReportingBaseReportResp, 'marketName' | 'channelGrouping'>

export type ReportingBaseTotalsResp = Pick<
  ReportingBaseReportResp,
  | 'sumConversionValue'
  | 'sumConversions'
  | 'sumCost'
  | 'sumClicks'
  | 'sumImpressions'
  | 'sumVideoViews'
  | 'sumViewableImpressions'
  | 'sumSessions'
  | 'sumDailyTarget'
  | 'sumDailyBudget'
>

export interface ReportingBaseTotalsGroupedByDateResp extends ReportingBaseTotalsResp {
  date: string
}

export interface ReportingBaseTotalsGroupedByDateAndBreakdownResp extends ReportingBaseTotalsGroupedByDateResp {
  marketName: string
  channelGrouping: string
}
export interface BudgetOptimiserResp {
  targetDate: string
  targetType: OptimisationStrategy
  optGroupName: string
  optGroup: string
  optFactorStr: string
  scenarioId?: string
  platform: BudgetOptimiserOptimalsPlatforms
  platformChannelGrouping: string
  platformChannelGroupingId?: string
  accountId: string
  campaignId: string
  campaignName: string
  daysWClicks: number
  isOptimizable: boolean
  spend: number
  clicks: number
  optSpend: number
  conversions: number
  newConversions: number
  optFitTotalNewConversions: number
  optClicks: number
  optPlatformConversions: number
  campaignNameOnly: string
  revenue: number
  optPlatformRevenue: number
  optFitTotalNewRevenue: number
  newRevenue: number
  groupOptFitRevenueConfidenceFactor: number
  rankIncrease: number
  rankDecrease: number
  spendHealth: string
  marginalCredit: number
  optMarginalCredit: number
  lookbackWindow: 30 | 60 | 90 | 120
}
export interface ReqOptimalsDetails {
  collection: 'bo'
  report: 'bo_optimals_v2'
  id: string
  clientId: number
  filters?: Filter[]
  fields?: Array<{ name: string; aggregation?: string }>
  orderBy?: {
    fields: DateField[]
    direction: Direction
  }
  targetCurrencyCode?: string
  limit?: number
}

export interface BudgetOptmisersTotalsResp {
  targetDate: string
  clientName: string
  platform: string
  platformChannelGrouping: string
  accountId: string
  campaignName: string
  campaignId: string
  spend: number
  conversions: number
  newConversions: number
  confidencePercent: number
  fittable: boolean
  hasLowVarSpend: boolean
  hasLowVarSpendEa: boolean
  hasHighMeanImpressionShare: boolean
  hasLowScoreR2: boolean
  isOptimizable: boolean
  isNotOptimizable: boolean
  isNotFittable: boolean
  campaignNameOnly: string
  jointRevenueConfidenceFactor: number
  jointConversionsConfidenceFactor: number
  newRevenue: number
  newOrderValue: number
  paramA: number
  paramB: number
  imprParamA: number
  imprParamB: number
  daysWClicks: number
  newConversionRate: number
  newClickConversionRate: number
  newImpressionConversionRate: number
  hasImpressionShare: boolean
  isImpressionCampaign: boolean
  newImpressionOrderValue: number
  meanEligibleAuctions: number
  targetCurrency: string
  fitQualityScore: number
  dataSufficiencyScore: number
  spendVariationScore: number
  conversionVolumeScore: number
  confidenceScore: number
  notOptimizableReasons: string[]
  lookbackWindow: 30 | 60 | 90 | 120
}

export interface ReqTotalDetails {
  collection: 'bo'
  report: 'bo_total_v2'
  fields?: Array<{ name: string }>
  id: string
  clientId: number
  filters: Filter[]
  targetCurrencyCode?: string
  limit?: number
}

export interface BudgetOptimiserPreprocResp {
  accountCurrency: string | null
  accountId: string
  campaignId: string
  campaignMame: string
  caseName: string | null
  clicks: number
  clicksEa: number
  conversions: number
  date: string
  eligibleAuctions: number
  fitClicks: number
  hasIs: boolean
  impressions: number
  platform: string
  revenue: number
  schemaVersion: string
  searchImpressionShare: number
  spend: number
  spendEa: number
  targetCurrency: string | null
  targetDate: string
  lookbackWindow: 30 | 60 | 90 | 120
}
export interface ReqPreprocDetails {
  collection: 'bo'
  report: 'bo_preproc_v2'
  fields?: Array<{ name: string }>
  id: string
  clientId: number
  filters: Filter[]
  targetCurrencyCode?: string
}

export interface CurrencyConversionRatesResp {
  date: string
  sourceDate: string
  baseCurrency: string
  targetCurrency: string
  exchangeRate: number
  runId: string
  lastUpdated: string
}
export interface ReqCurrencyConversionRatesDetails {
  collection: 'conversion_rates'
  report: 'conversion_rates'
  fields?: Array<{ name: string }>
  id: string
  clientId: number
  filters: Filter[]
}

export const API_CONFIG_STATUSES = {
  failed: 'failed',
  completed: 'completed',
  fetch_failed: 'fetch_failed',
  running: 'running',
  queued: 'queued'
} as const

export type E2EStatus = (typeof API_CONFIG_STATUSES)[keyof typeof API_CONFIG_STATUSES]

export interface ConfigStatusResp {
  clientId: number
  configId: string
  error: string
  lastUpdated: string
  message: string | null
  payload: string
  report: string
  service: string
  status: E2EStatus
  statusCode: number | null
  targetDataset: string
  targetProjectId: string
  taskId: string
  errorCode: ErrorCodeType | null
  errorMessage: string | null
}

export type DagService = 'attribution_etl' | 'attribution_model' | 'connectors' | 'transform' | 'bo'
export interface DagChildrenStatusItem {
  children: DagChildrenStatusItem[]
  configId: string
  error: string
  errorCode: ErrorCodeType | null
  errorMessage: string | null
  lastUpdated: string
  message: string
  service: DagService
  status: E2EStatus
}

export interface DagStatusResp {
  children: DagChildrenStatusItem
  clientId: number
  config: string
  configId: string
  error: string
  errorCode: ErrorCodeType | null
  errorMessage: string | null
  lastUpdated: string
  message: string
  service: DagService
  status: E2EStatus
}

export interface EnhancedSignalsEventsResp {
  targetCurrencyCode: string
  publishTime: string
  publishTimeTimestamp: string
  transactionId: string
  currency: string
  name: string
  market: string
  unmatchedProductIds: string[] | null
  value: number
}
export interface EnhancedSignalsMarketsResp {
  distinctMarket: string
}

export interface EnhancedSignalsAggregatedEventsResp {
  targetCurrencyCode: string
  publishTime: string
  countTransactionId: number
  sumValue: number
}

//* ** Creative Reporting */
export interface CreativePerformanceReportResponse {
  accountId: string
  adId: string
  adName: string
  adType: string
  campaignType: string
  campaignId: string
  campaignName: string
  adsetName: string
  audienceGrouping: string
  adConceptGrouping: string
  adFormatGrouping: string
  testGrouping: string
  customGrouping1: string
  customGrouping2: string
  customGrouping3: string
  customGrouping4: string
  date: string
  maxFirstImageName: string
  maxFirstImageUrl: string
  funnelGrouping: string
  marketName: string
  platform: string
  sumAdSpend: number
  sumClicks: number
  sumClicks15To28Days: number
  sumClicks1To14Days: number
  sumClicks29To42Days: number
  sumImpressions: number
  sumImpressions15To28Days: number
  sumImpressions1To14Days: number
  sumImpressions29To42Days: number
  targetCurrencyCode: string
  sumCalculatedConversions: number
  sumCalculatedConversionValue: number
  sumVideoViews: number
  sumViewableImpressions: number
  sumVideoAvgTimeWatchedActions: number
  countVideoAvgTimeWatchedActions: number
  sumVideoP25WatchedActions: number
  sumVideoP50WatchedActions: number
  sumVideoP75WatchedActions: number
  sumVideoP100WatchedActions: number
  sumVideoWatch2S: number
  sumVideoWatch6S: number
  sumQualifiedVideoViews: number
  sumOriginalVideoViews: number
  avgTiktokCreativeScore: number | null
}

export interface FacebookVideoAsset {
  id: string
  picture: string
  status: string
  createdTime: string
  updatedTime: string
}

export interface FacebookImageAsset {
  id: string
  url: string
  status: string
  createdTime: string
  updatedTime: string
}

export interface TikTokImageAsset {
  imageId: string
  imageUrl: string
  createTime: string
  modifyTime: string
}

export interface TikTokVideoAsset {
  videoId: string
  videoCoverUrl: string
  url: string
  createTime: string
  modifyTime: string
}

export interface PinterestImageAsset {
  id: string
  url: string
  status: string
  createdAt: string
  updatedAt: string
}

export interface PinterestVideoAsset {
  id: string
  url: string
  thumbnailUrl: string
  durationMs: number
  width: string
  height: string
  createdAt: string
  updatedAt: string
}

export interface SnapchatImageAsset {
  id: string
  name: string
  downloadLink: string
  createdAt: string
  updatedAt: string
}

export interface SnapchatVideoAsset {
  id: string
  name: string
  downloadLink: string
  thumbnailLink: string
  createdAt: string
  updatedAt: string
}

export interface GadsImageAsset {
  id: string
  url?: string
  downloadUrl?: string
  name?: string
  width?: string
  height?: string
}

export interface GadsVideoAsset {
  id: string
  url?: string
  downloadUrl?: string
  thumbnailUrl?: string
  youtubeVideoId?: string
  name?: string
  title?: string
}

export interface PlatformCreativeReportResponse<
  TImageAsset extends FacebookImageAsset | TikTokImageAsset | PinterestImageAsset | SnapchatImageAsset | GadsImageAsset,
  TVideoAsset extends FacebookVideoAsset | TikTokVideoAsset | PinterestVideoAsset | SnapchatVideoAsset | GadsVideoAsset
> {
  id: string
  adId: string
  adName: string
  accountId?: string
  imageAssets: TImageAsset[]
  videoAssets: TVideoAsset[]
  objectStorySpec?: string
  pinId?: string
  creativeId?: string
}

export const postAttributionModelMetricsReport = async (
  data: AnalyticsReq,
  token?: string
): Promise<AttributionModelsMetricsResp> => {
  return await client.post<AttributionModelsMetricsResp>({
    path: paths.analytics,
    data,
    token
  })
}

export const postFeed = async (data: AnalyticsReq, token?: string): Promise<FeedResp[]> => {
  return await client.post<FeedResp[]>({
    path: paths.analytics,
    data,
    token
  })
}

export const postReport = async <T = ReportingBaseReportResp[]>(data: AnalyticsReq, token?: string): Promise<T> => {
  return await client.post<T>({
    path: paths.analytics,
    data,
    token
  })
}

export const postDashboardsAnalyticsReport = async (data: AnalyticsReq, token?: string): Promise<AnalyticsResp> => {
  return await client.post<AnalyticsResp>({
    path: paths.analytics,
    data,
    token,
    rawResponse: true
  })
}

export function getDimensionValues(
  params: DimensionValuesReq & { includeNulls: true },
  token?: string
): Promise<DimensionValuesWithNullsResp>
export function getDimensionValues(
  params: DimensionValuesReq & { includeNulls?: false },
  token?: string
): Promise<DimensionValuesResp>
export function getDimensionValues(
  params: DimensionValuesReq,
  token?: string
): Promise<DimensionValuesResp | DimensionValuesWithNullsResp>
export async function getDimensionValues(
  params: DimensionValuesReq,
  token?: string
): Promise<DimensionValuesResp | DimensionValuesWithNullsResp> {
  const query = new URLSearchParams({
    client_id: params.clientId.toString(),
    collection: params.collection,
    report: params.report,
    dimension: params.dimension
  })
  if (params.configId != null && params.configId !== '') {
    query.set('config_id', params.configId)
  }
  if (params.limit != null) {
    query.set('limit', String(params.limit))
  }
  if (params.includeNulls === true) {
    query.set('include_null', 'true')
  }

  return await client.get<DimensionValuesResp | DimensionValuesWithNullsResp>({
    path: `${paths.dimensionValues}?${query.toString()}`,
    token,
    rawResponse: true
  })
}

export const postBudgetOptimiser = async (data: ReqOptimalsDetails, token?: string): Promise<BudgetOptimiserResp[]> => {
  return await client.post<BudgetOptimiserResp[]>({
    path: paths.analytics,
    data,
    token
  })
}

export const postBudgetOptimiserTotals = async (
  data: ReqTotalDetails,
  token?: string
): Promise<BudgetOptmisersTotalsResp[]> => {
  return await client.post<BudgetOptmisersTotalsResp[]>({
    path: paths.analytics,
    data,
    token
  })
}

export const postBudgetOptimiserPreproc = async (
  data: ReqPreprocDetails,
  token?: string
): Promise<BudgetOptimiserPreprocResp[]> => {
  return await client.post<BudgetOptimiserPreprocResp[]>({
    path: paths.analytics,
    data,
    token
  })
}

export const postAttribution = async (data: AnalyticsReq, token?: string): Promise<AttributionResponse[]> => {
  return await client.post<AttributionResponse[]>({
    path: paths.analytics,
    data,
    token
  })
}

export const postAttributionChannelAggregated = async (
  data: AnalyticsReq,
  token?: string
): Promise<AttributionChannelAggregatedResponse[]> => {
  return await client.post<AttributionChannelAggregatedResponse[]>({
    path: paths.analytics,
    data,
    token
  })
}

export const postAttributionChannelAggregatedV2 = async (
  data: AnalyticsReq,
  token?: string
): Promise<AttributionChannelAggregatedResponseV2[]> => {
  return await client.post<AttributionChannelAggregatedResponseV2[]>({
    path: paths.analytics,
    data,
    token
  })
}

export const postAttributionDateChannelAggregated = async (
  data: AnalyticsReq,
  token?: string
): Promise<AttributionDateChannelAggregatedResponse[]> => {
  return await client.post<AttributionDateChannelAggregatedResponse[]>({
    path: paths.analytics,
    data,
    token
  })
}

export const postAttributionDateChannelAggregatedV2 = async (
  data: AnalyticsReq,
  token?: string
): Promise<AttributionDateChannelAggregatedResponseV2[]> => {
  return await client.post<AttributionDateChannelAggregatedResponseV2[]>({
    path: paths.analytics,
    data,
    token
  })
}

export const postGetDatesInAttributionReport = async (
  data: AnalyticsReq,
  token?: string
): Promise<DatesInAttributionReportResp[]> => {
  return await client.post<DatesInAttributionReportResp[]>({
    path: paths.analytics,
    data,
    token
  })
}

export const postGetDatesInBudgetOptimizerOptimals = async (
  data: ReqOptimalsDetails,
  token?: string
): Promise<DatesInBudgetOptimiserOptimalsResp[]> => {
  return await client.post<DatesInBudgetOptimiserOptimalsResp[]>({
    path: paths.analytics,
    data,
    token
  })
}

export const postGetCurrencyConversionRates = async (
  data: ReqCurrencyConversionRatesDetails,
  token?: string
): Promise<CurrencyConversionRatesResp[]> => {
  return await client.post<CurrencyConversionRatesResp[]>({
    path: paths.analytics,
    data,
    token
  })
}

export const postConfigStatuses = async (
  reportType: ReportType,
  clientId: number,
  useCache = false
): Promise<ConfigStatusResp[]> => {
  return await client.post<ConfigStatusResp[]>({
    path: paths.analytics,
    data: {
      clientId,
      collection: 'status',
      report: reportType,
      useCache
    }
  })
}

export const postConfigStatusById = async <T>(
  reportType: ReportType,
  clientId: number,
  configId: number,
  useCache = false
): Promise<T> => {
  return await client.post<T>({
    path: paths.analytics,
    data: {
      clientId,
      collection: 'status',
      report: reportType,
      id: configId,
      useCache
    }
  })
}

export const postEnhancedSignalsEvents = async (data: AnalyticsReq, token?: string): Promise<AnalyticsResp> => {
  return await client.post<AnalyticsResp>({
    path: paths.analytics,
    data,
    token,
    rawResponse: true
  })
}

export const postChannelGroupingReport = async <T>(data: Omit<AnalyticsReq, 'collection'>): Promise<T[]> => {
  return await client.post<T[]>({
    path: paths.analytics,
    data: {
      ...data,
      collection: 'channel_grouping'
    },
    rawResponse: true
  })
}

//* ** Creative Reporting */
export const postCreativePerformanceReport = async (
  data: AnalyticsReq
): Promise<CreativePerformanceReportResponse[]> => {
  return await client.post<CreativePerformanceReportResponse[]>({
    path: paths.analytics,
    data
  })
}

export const postPlatformCreativeReport = async <
  TImageAsset extends FacebookImageAsset | TikTokImageAsset | PinterestImageAsset | SnapchatImageAsset | GadsImageAsset,
  TVideoAsset extends FacebookVideoAsset | TikTokVideoAsset | PinterestVideoAsset | SnapchatVideoAsset | GadsVideoAsset
>(
  data: AnalyticsReq
): Promise<Array<PlatformCreativeReportResponse<TImageAsset, TVideoAsset>>> => {
  return await client.post<Array<PlatformCreativeReportResponse<TImageAsset, TVideoAsset>>>({
    path: paths.analytics,
    data
  })
}

export interface ReqBoScenarioDetails {
  collection: 'bo'
  report: 'bo_scenario'
  id: string
  clientId: number
  filters: Filter[]
  fields?: Array<{ name: string }>
  cursor?: number
  limit?: number
  target_currency_code?: string
}

export interface BoScenarioResp {
  data: Array<Record<string, unknown>>
  totalRows: number
  nextCursor: string | number
  previousCursor: string | number
  statusCode: number
}

export const postBudgetOptimiserScenario = async (
  data: ReqBoScenarioDetails,
  token?: string
): Promise<BoScenarioResp> => {
  return await client.post<BoScenarioResp>({
    path: paths.analytics,
    data,
    token,
    rawResponse: true
  })
}
